import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People,
  Quiz,
  Assessment,
  TrendingUp,
  Add,
  Visibility
} from '@mui/icons-material';
import { ResponsiveContainer } from 'recharts';
import axios from 'axios';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joinRequests, setJoinRequests] = useState([]);
  const [updating, setUpdating] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
    fetchJoinRequests();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data.stats);
    } catch (error) {
      setError('Failed to load dashboard statistics');
      console.error('Dashboard stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJoinRequests = async () => {
    try {
      // fetch only pending requests for now
      const { data } = await axios.get('/api/join-requests', { params: { status: 'pending' } });
      setJoinRequests(data.requests || []);
    } catch (e) {
      // keep silent in UI, show in console
      console.error('Failed to load join requests', e);
    }
  };

  const actOnRequest = async (id, action) => {
    try {
      setUpdating(prev => ({ ...prev, [id]: true }));
      await axios.patch(`/api/join-requests/${id}`, { action });
      setJoinRequests(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      console.error('Failed to update join request', e);
    } finally {
      setUpdating(prev => ({ ...prev, [id]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Category visualizations removed

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.main`,
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back! Here's an overview of your academy's MCQ system.
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={stats?.totalStudents || 0}
            icon={<People />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total MCQs"
            value={stats?.totalMCQs || 0}
            icon={<Quiz />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tests Taken"
            value={stats?.totalTests || 0}
            icon={<Assessment />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Score"
            value={`${stats?.averageScore || 0}%`}
            icon={<TrendingUp />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Category chart removed */}

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                onClick={() => navigate('/admin/mcqs')}
                sx={{ py: 1.5 }}
              >
                Add New MCQ
              </Button>
              <Button
                variant="outlined"
                startIcon={<Quiz />}
                fullWidth
                onClick={() => navigate('/admin/mcqs')}
                sx={{ py: 1.5 }}
              >
                Manage MCQs
              </Button>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                fullWidth
                onClick={() => navigate('/results')}
                sx={{ py: 1.5 }}
              >
                View All Results
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Test Results */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Test Results
            </Typography>
            {stats?.recentTests && stats.recentTests.length > 0 ? (
              <List>
                {stats.recentTests.slice(0, 10).map((test, index) => (
                  <ListItem
                    key={test.id}
                    divider={index < stats.recentTests.length - 1}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <ListItemText
                      primary={test.userName || 'Unknown Student'}
                      secondary={`${test.category} • ${formatDate(test.submittedAt)}`}
                    />
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={`${test.score}%`}
                        color={test.score >= 80 ? 'success' : test.score >= 60 ? 'warning' : 'error'}
                        variant="filled"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {test.correctAnswers}/{test.totalQuestions}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <Typography color="text.secondary">No test results available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Pending Join Requests */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">Pending Test Join Requests</Typography>
              <Button size="small" onClick={fetchJoinRequests}>Refresh</Button>
            </Box>
            {joinRequests.length > 0 ? (
              <List>
                {joinRequests.map((req, index) => (
                  <ListItem key={req.id} divider={index < joinRequests.length - 1} secondaryAction={
                    <Box display="flex" gap={1}>
                      <Button variant="contained" size="small" onClick={() => actOnRequest(req.id, 'approve')} disabled={!!updating[req.id]}>Approve</Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => actOnRequest(req.id, 'reject')} disabled={!!updating[req.id]}>Reject</Button>
                    </Box>
                  }>
                    <ListItemText
                      primary={`${req.student_name}`}
                      secondary={`Requested at: ${new Date(req.requested_at).toLocaleString()} • Test: ${req.test_id}`}
                    />
                    <Chip size="small" label={req.status} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={120}>
                <Typography color="text.secondary">No pending join requests</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminDashboard; 